import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useContainerQuery } from 'react-container-query';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';

import { DEFAULT_LANGUAGE } from 'cityads-v3/i18n/utils';
import { USER_ROLE_CODE_WEBMASTER } from 'cityads-v3/reducers/auth';

import FacebookLink from './links/Facebook';
import InstagramLink from './links/Instagram';
import LinkedInLink from './links/LinkedIn';
import TelegramLink from './links/Telegram';
import TwitterLink from './links/Twitter';
// import VKLink from './links/VK';

import AKARIcon from './partners/AKAR';
import CashbeIcon from './partners/Cashbe'
import IabIcon from './partners/Iab';
import HuaweiIcon from './partners/Huawei';
// import SkolkovoEnIcon from './partners/SkolkovoEn';
import SkolkovoRuIcon from './partners/SkolkovoRu';

import badgesTi2020 from './badges/ti2020';

import classes from './Footer.scss';

const responsiveQuery = {
  [classes.footerSmall]: {
    maxWidth: 496,
  },
  [classes.footerLarge]: {
    minWidth: 1232,
  },
}

export default function Footer({ className }) {
  const { t } = useTranslation(['main']);
  const location = useLocation()
  const user = useSelector(state => state.auth.user);
  const language = useSelector(state => state.language.language);
  const showFooter = useSelector(state => state.header.showFooter);
  const [responsiveClasses, containerRef] = useContainerQuery(responsiveQuery);

  if (!showFooter) {
    return null;
  }

  const isRussianLanguage = language === 'ru';
  const showBlogLink = ['ru', 'en'].includes(language);
  const showCashbeLogo = (
    ['pt', 'en'].includes(language)
    && location.pathname === '/main'
  )
  const agreementUrl = getAgreement(language);
  const privacyPolicy_url = getPrivacyPolicy(language);
  const year = (new Date()).getFullYear();

  return (
    <footer id="footer" className={classNames(classes.footer, responsiveClasses, className)} ref={containerRef}>
      <div className={classes.socialaddress}>
        <div className={classes.social}>
          {isRussianLanguage ?
            (
              <>
                {/*
                <VKLink />
                */}
                <LinkedInLink />
                <TelegramLink />
                <TwitterLink />
              </>
            ) :
            (
              <>
                <LinkedInLink />
                <InstagramLink />
                <FacebookLink />
                <TwitterLink />
              </>
            )
          }
        </div>

        {/*
        <div className={classes.address}>
          {tArray(t, 'footer.address', 'p')}
        </div>
        */}
      </div>

      <div className={classes.partners}>
        <a
          href="https://developer.huawei.com/consumer/en/doc/distribution/promotion/partnerinformation-0000001058393913"
          target="_blank"
          rel="noopener noreferrer"
        >
          <HuaweiIcon height={40} role="img" title={t('footer.huawei')} />
        </a>

        {isRussianLanguage ?
          <AKARIcon className={classes.partnerIcon} role="img" title={t('footer.akarMember')} /> :
          null
        }
        <IabIcon className={classes.partnerIcon} role="img" title={t('footer.iabMember')} />

        {showCashbeLogo && (
          <a
            href="https://cashbe.com.br/"
            target="_blank"
            rel="noopener noreferrer nofollow"
            className={classes.partnerIcon}
          >
            <CashbeIcon role="img" alt={t('footer.cashbe')} title={t('footer.cashbe')} />
          </a>
        )}

        {isRussianLanguage ?
          (
            <a href="https://sk.ru" target="_blank" rel="noopener noreferrer">
              <SkolkovoRuIcon className={classNames(classes.partnerIcon, classes.skolkovoIcon)} role="img" title={t('footer.skolkovoParticipant')} />
            </a>
          )
          :
          null
        }

        <div className={classes.badges}>
          <img src={badgesTi2020[language] ?? badgesTi2020[DEFAULT_LANGUAGE]} alt={t('footer.badges.ti2020')} className={classes.badge} />
        </div>
      </div>

      <ul className={classes.menu}>
        <li className={classes.menuItem}>
          <Link className={classes.menuLink} to="/main/register/publisher">{t('footer.startWork')}</Link>
        </li>
        {showBlogLink && (
          <li className={classes.menuItem}>
            <Link className={classes.menuLink} to="/main/news/all">{t('footer.blog')}</Link>
          </li>
        )}
        <li className={classes.menuItem}>
          <Link className={classes.menuLink} to="/main/about/contacts">{t('footer.contacts')}</Link>
        </li>
        <li className={classes.menuItem}>
          <Link className={classes.menuLink} to="/main/about/vacancy">{t('footer.vacancy')}</Link>
        </li>
        <li className={classes.menuItem}>
          <Link className={classes.menuLink} to="/publisher/api/description">{t('footer.documentationApi')}</Link>
        </li>
        {((!user || user.user_role_code === USER_ROLE_CODE_WEBMASTER) && agreementUrl) ?
          (
            <li className={classes.menuItem}>
              <a href={agreementUrl} className={classes.menuLink}>{t('footer.oferta')}</a>
            </li>
          )
          :
          null
        }
        <li className={classes.menuItem}>
          <a className={classes.menuLink} href={privacyPolicy_url}>{t('footer.privacyPolicy')}</a>
        </li>
        {/*
          Нужно будет сделать на английском и польском
        <li className={classes.menuItem}>
          <span className={classes.menuLink}>Opt-Out</span>
        </li>
        */}
      </ul>
      <div className={classes.copyright}>
        © {year} CityAds Media
      </div>
    </footer>
  );
}

Footer.propTypes = {
  className: PropTypes.string,
};

export function getAgreement(language = 'en') {
  switch (language) {
    default:
    case 'en':
      return '/static/documents/agreements/publisher-rules.utheram.20190205.pdf';
    case 'pl':
      return '/static/documents/agreements/publisher-rules.poland.20180525.pdf';
    case 'pt':
      return '/static/documents/agreements/publisher-rules.brazil.20151201.pdf';
    case 'ru':
      return null;
    case 'zh':
      return '/static/documents/agreements/publisher-rules.china.20150601.pdf';
    case 'es':
      return '/static/documents/agreements/publisher-rules.spain.20150601.pdf';
    case 'my':
      return '/static/documents/agreements/publisher-rules.malaysia.20140601.pdf';
  }
}

export function getPrivacyPolicy(language = 'en') {
  switch (language) {
    default:
    case 'en':
      return '/static/documents/privacy-policy/privacy-policy.en.20250804.pdf';
    case 'ru':
      return '/static/documents/privacy-policy/privacy-policy.ru.20220117.pdf';
  }
}
